import { useCallback, useEffect, useState } from "react";

import type { FileWithPath } from "react-dropzone";

import usePrevious from "../../hooks/usePrevious";
import DropArea from "../DropArea/DropArea";
import FileList from "../FileList/FileList";

import style from "./FilePicker.module.css";

interface DropZoneProps {
  title: React.ReactNode;
  maxFiles: number;
  maxFileSizeInMB: number;
  acceptedFileExtensions: string[];
  disabled?: boolean;
  onChange: (files: FileWithPath[]) => void;
}

type FileMap = {
  [key: string]: FileWithPath;
};

const FilePicker: React.FC<DropZoneProps> = ({
  title,
  maxFiles,
  maxFileSizeInMB,
  acceptedFileExtensions,
  disabled,
  onChange,
}) => {
  const [files, setFiles] = useState<FileMap>({});
  const previousFiles = usePrevious(files);
  useEffect(() => {
    if (files !== previousFiles) {
      onChange(Object.values(files));
    }
  }, [onChange, previousFiles, files]);

  const maxFilesReached = Object.keys(files).length >= maxFiles;

  const addFile = useCallback((file: FileWithPath) => {
    setFiles((oldFiles) => {
      const filesCopy = { ...oldFiles };
      if (file.path) {
        filesCopy[file.path] = file;
      }
      return filesCopy;
    });
  }, []);

  const removeFile = useCallback((filePath: string | undefined) => {
    setFiles((oldFiles) => {
      const filesCopy = { ...oldFiles };
      if (filePath) {
        delete filesCopy[filePath];
      }
      return filesCopy;
    });
  }, []);

  const subTitle = `Maximum ${maxFiles} files / ${maxFileSizeInMB}MB per each, supported file formats: ${acceptedFileExtensions.join(
    ", "
  )}`;

  return (
    <section className={style.container}>
      <div className={style.title}>{title}</div>
      <div className={style.subTitle}>{subTitle}</div>
      <DropArea
        onChange={addFile}
        maxFileSizeInMB={maxFileSizeInMB}
        maxFiles={maxFiles}
        acceptedFileExtensions={acceptedFileExtensions}
        disabled={disabled}
        maxFilesReached={maxFilesReached}
      />
      <aside>
        <FileList files={Object.values(files)} removeFile={removeFile} disabled={disabled} />
      </aside>
    </section>
  );
};

export default FilePicker;
